import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { usePractice } from '../contexts/PracticeContext';
import PracticeSelector from '../components/PracticeSelector';
import PracticeDetails from '../components/PracticeDetails';
import Transactions from './Transactions';
import UsersTabContent from '../components/OrganizationUsers/UsersTabContent';
import InvitationsTabContent from '../components/OrganizationUsers/InvitationsTabContent';

// Wrapper component for Transactions that hides the title and adjusts spacing
function TransactionsTabContent() {
  return (
    <Box sx={{ 
      '& .MuiContainer-root': { 
        mt: 0,
        '& h4:first-of-type': { display: 'none' }
      } 
    }}>
      <Transactions />
    </Box>
  );
}

// Wrapper component for Users tab content
function UsersTabContentWrapper() {
  const { selectedPractice } = usePractice();
  
  if (!selectedPractice) {
    return (
      <Container disableGutters>
        <Typography variant="body1">
          Please select an organization to view its users
        </Typography>
      </Container>
    );
  }

  return <UsersTabContent organizationId={selectedPractice.id} />;
}

// Wrapper component for Invitations tab content
function InvitationsTabContentWrapper() {
  const { selectedPractice } = usePractice();
  
  if (!selectedPractice) {
    return (
      <Container disableGutters>
        <Typography variant="body1">
          Please select an organization to view its invitations
        </Typography>
      </Container>
    );
  }

  return <InvitationsTabContent organizationId={selectedPractice.id} />;
}

export default function Practice() {
  const { selectedPractice } = usePractice();
  const { tab } = useParams();
  const navigate = useNavigate();

  // Determine initial tab from URL: 'transactions' maps to index 1, 'users' maps to index 2, 'invitations' maps to index 3, else index 0 (details)
  const getTabIndex = () => {
    if (tab === 'transactions') return 1;
    if (tab === 'users') return 2;
    if (tab === 'invitations') return 3;
    return 0;
  };

  const [tabIndex, setTabIndex] = React.useState(getTabIndex());

  // Sync state when URL param changes
  React.useEffect(() => {
    setTabIndex(getTabIndex());
  }, [tab]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Update URL to allow linking to a specific tab
    if (newValue === 0) {
      navigate('/practice');
    } else if (newValue === 1) {
      navigate('/practice/transactions');
    } else if (newValue === 2) {
      navigate('/practice/users');
    } else if (newValue === 3) {
      navigate('/practice/invitations');
    }
  };

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ lineHeight: 1.2 }}>
          Practice
        </Typography>
        <Box sx={{ minWidth: 200, maxWidth: 480, display: 'flex', alignItems: 'flex-end' }}>
          <PracticeSelector centered={false} />
        </Box>
      </Box>
      {!selectedPractice && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a practice to view details.
        </Typography>
      )}

      {!selectedPractice ? (
        <Card>
          <CardContent>
            <Typography variant="body1">
              No practice selected.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Details" />
            <Tab label="Transactions" />
            <Tab label="Org's Users" />
            <Tab label="Invitations" />
          </Tabs>
          {tabIndex === 0 && <PracticeDetails />}
          {tabIndex === 1 && <TransactionsTabContent />}
          {tabIndex === 2 && <UsersTabContentWrapper />}
          {tabIndex === 3 && <InvitationsTabContentWrapper />}
        </>
      )}
    </Container>
  );
}
